<template>
    <modal
        :show="show"
        dismissable
        body-classes="p-0"
        modal-classes="modal-lg"
        @update:show="$emit('update:show', $event)"
    >
        <card class="card-form mb-0">
            <template slot="header">
                <h2 class="modal-title">Storage Cost Summary</h2>
                <button
                    type="button"
                    class="close"
                    aria-label="Close"
                    @click="$emit('update:show', false)"
                >
                    <i class="tim-icons icon-simple-remove" />
                </button>
            </template>
            <div :class="$style.summary">
                <h4>Delivery Date</h4>
                <el-date-picker
                    v-model="deliveryDate"
                    :class="$style.datePicker"
                    :picker-options="{ disabledDate: isDeliveryDateInvalid }"
                    format="MM/dd/yyyy"
                    type="date"
                    placeholder="Select date"
                />
            </div>
            <div :class="$style.summary">
                <h4>Total</h4>
                <h4 :class="$style.value">{{ total }}</h4>
            </div>
            <el-table
                :data="receiptLogs"
                :class="$style.table"
                :row-class-name="$style.row"
                :header-cell-class-name="$style.headerCell"
            >
                <el-table-column prop="id" label="Receipt No." />
                <el-table-column label="Storage Start">
                    <template slot-scope="{ row }">
                        {{ storageStartFormatted(row) }}
                    </template>
                </el-table-column>
                <el-table-column label="Days In Storage">
                    <template slot-scope="{ row }">
                        {{ storageDaysForDeliveryDate(row) }}
                    </template>
                </el-table-column>
                <el-table-column label="Storage Cost">
                    <template slot-scope="{ row }">
                        {{ feesAccruedFormatted(row, null, deliveryDate) }}
                    </template>
                </el-table-column>
            </el-table>
        </card>
    </modal>
</template>

<script>
import moment from "moment-timezone";
import { mapActions } from "vuex";
import { Modal } from "src/components/UI";
import { DatePicker } from "element-ui";
import storageFees from "src/mixins/storageFees";

export default {
    name: "StorageCostSummary",
    components: {
        Modal,
        [DatePicker.name]: DatePicker,
    },
    mixins: [storageFees],
    props: {
        show: {
            type: Boolean,
            required: true,
        },
        receiptLogIds: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            receiptLogs: [],
            deliveryDate: new Date(),
        };
    },
    computed: {
        total() {
            const total = this.receiptLogs
                .map((log) => this.feesAccrued(log, null, this.deliveryDate))
                .reduce((a, b) => a + b, 0);
            return this.formatNumberToCurrency(total);
        },
    },
    watch: {
        show: {
            immediate: true,
            async handler(val) {
                if (val) {
                    this.receiptLogs = await this.fetchReceiptLogsCalculateCost(
                        this.receiptLogIds,
                    );
                } else {
                    this.receiptLogs = [];
                    this.deliveryDate = new Date();
                }
            },
        },
    },
    methods: {
        ...mapActions("receiptLog", ["fetchReceiptLogsCalculateCost"]),
        isDeliveryDateInvalid(date) {
            const today = new Date(new Date().setHours(0, 0, 0, 0));
            return today > date;
        },
        storageStartFormatted(receiptLog) {
            // sort ascending on date
            const units = receiptLog.units
                .filter((unit) => unit.date)
                .sort((a, b) => new Date(a.date) - new Date(b.date));
            const numberOfDates = [...new Set(units.map((a) => a.date))];
            const start = this.storageStart(receiptLog, units[0]);
            if (start) {
                if (numberOfDates.length === 1) {
                    return start.format("MM/DD/YYYY");
                }
                return `${start.format("MM/DD/YYYY")} + ${numberOfDates.length - 1} other dates`;
            }
            return "--";
        },
        storageDaysForDeliveryDate(row) {
            const storageStart = this.storageStart(row, row.units[0]);
            // inclusive of start
            const daysPast =
                Math.floor(
                    moment(this.deliveryDate).diff(storageStart, "days", true),
                ) + 1;
            return daysPast > 0 ? daysPast : 0;
        },
    },
};
</script>

<style module>
.summary {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
}
.summary h4.value {
    color: #666;
}
.datePicker {
    flex-basis: 150px;
}
.row:hover td {
    background: transparent !important;
}
.row td {
    border-top: none !important;
}
.table table {
    margin-bottom: 0;
}
.headerCell {
    padding-bottom: 0px !important;
}
</style>
